import * as React from "react";
import { Link } from "react-router";

import SurahName from "@/components/common/SurahName";
import type { RubWithSurah } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useCurrentJuzForStages } from "@/store/settings";

interface RubsViewProps {
	rubs: RubWithSurah[];
}

const JUZ_NUMBERS = Array.from({ length: 30 }, (_, i) => i + 1);

// tracking-wide on phones so "Broj stranice" stays on one line at 360px
const dtClass =
	"text-[11px] font-medium uppercase tracking-wide text-ink-subtle sm:tracking-wider";

const RubsView: React.FC<RubsViewProps> = ({ rubs }) => {
	const { currentJuzForStages, setCurrentJuzForStages } = useCurrentJuzForStages();

	const juzRubs = React.useMemo(
		() =>
			rubs
				.filter((rub) => rub.juz === currentJuzForStages)
				.sort((a, b) => a.page - b.page),
		[rubs, currentJuzForStages],
	);

	return (
		<section className="space-y-6 py-6">
			<div>
				<h3
					id="juz-selector-label"
					className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-subtle"
				>
					Džuz
				</h3>
				<div
					role="group"
					aria-labelledby="juz-selector-label"
					className="grid grid-cols-6 gap-1.5 sm:grid-cols-10 lg:grid-cols-15"
				>
					{JUZ_NUMBERS.map((juzNum) => {
						const selected = currentJuzForStages === juzNum;
						return (
							<button
								type="button"
								key={juzNum}
								aria-pressed={selected}
								onClick={() => setCurrentJuzForStages(juzNum)}
								className={cn(
									"pressable h-11 rounded-control text-sm tabular-nums ring-1 md:h-10",
									selected
										? "bg-emerald-900 font-semibold text-paper-50 shadow-card ring-emerald-900"
										: "bg-paper-50 text-ink-muted ring-paper-300 hover:bg-paper-100 hover:text-emerald-900 hover:ring-emerald-800/30",
								)}
							>
								{juzNum}
							</button>
						);
					})}
				</div>
			</div>

			{juzRubs.length > 0 ? (
				<ul className="grid grid-cols-1 gap-3">
					{juzRubs.map((rub) => {
						const stage = currentJuzForStages * 8 + 1 - rub.hizb_quarter;

						return (
							<li key={rub.number}>
								<Link
									to={`/${rub.page}`}
									className="pressable group block rounded-card bg-paper-50 shadow-card ring-1 ring-paper-300 transition-shadow [--press-scale:0.985] hover:shadow-card-hover hover:ring-gold-400/60"
								>
									<p
										dir="rtl"
										lang="ar"
										className="truncate px-5 pb-2 pt-4 text-center font-arabic text-[28px] leading-[1.7] text-ink md:text-[34px]"
									>
										{rub.text}
									</p>
									<div aria-hidden className="mx-5 ornament-rule" />
									<dl className="grid grid-cols-3 divide-x divide-paper-300/80 py-3 text-center">
										<div className="min-w-0 px-1 sm:px-2">
											<dt className={dtClass}>Broj kruga</dt>
											<dd className="mt-0.5 text-sm font-semibold tabular-nums text-emerald-900">
												{stage}
											</dd>
										</div>
										<div className="min-w-0 px-1 sm:px-2">
											<dt className={dtClass}>Broj stranice</dt>
											<dd className="mt-0.5 text-sm font-semibold tabular-nums text-ink">
												{rub.page}
											</dd>
										</div>
										<div className="flex min-w-0 items-center justify-center px-1 sm:px-2">
											<dt className="sr-only">Sura</dt>
											<dd className="flex min-w-0 items-center gap-2">
												<SurahName
													number={rub.surah.id}
													nameArabic={rub.surah.name_arabic}
													className="hidden text-[26px] sm:inline"
												/>
												<span className="min-w-0 text-left">
													<span className={cn("block truncate", dtClass)}>
														{rub.surah.name_simple}
													</span>
													<span className="block truncate text-sm font-semibold text-ink">
														{rub.surah.name_bosnian}
													</span>
												</span>
											</dd>
										</div>
									</dl>
								</Link>
							</li>
						);
					})}
				</ul>
			) : (
				<p className="rounded-card bg-paper-100 px-4 py-6 text-center text-sm text-ink-muted ring-1 ring-paper-300">
					Nema krugova za ovaj džuz.
				</p>
			)}
		</section>
	);
};

export default React.memo(RubsView);
